import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { QueryWebEventsResponse, WebEvent, WebEventsSort, WebEventsSortField } from '@playq/octopus2-admin';
import { GenericFailure } from '@playq/services-shared';

import {
  IUseQueryOptionsExtended,
  useEitherQuery,
  useMutateQueryDataAfterEntityDeleting,
  useQueryPrefetch,
} from '/api/service-hooks';
import { services2 } from '/api/services2';
import { IQuery } from '/common/models';

import { webEventsQueryKeys } from './constants';

const emptyArray: [] = [];

export const useWebEventsQuery = (
  query: IQuery<WebEventsSortField, WebEventsSort>,
  options?: IUseQueryOptionsExtended<QueryWebEventsResponse | undefined, GenericFailure | Error>
) => {
  const queryClient = useQueryClient();

  const { iterator, sortBy, filterBy } = query;

  const keys = useMemo(() => webEventsQueryKeys.concat(iterator, sortBy, filterBy), [iterator, sortBy, filterBy]);

  const { mutate, refetch, ...res } = useEitherQuery(
    keys,
    () => services2.webEventsService.queryWebEvents(iterator, sortBy, filterBy),
    {
      placeholderData: keepPreviousData,
      ...options,
    }
  );

  const total = useMemo(() => res.data?.total ?? 0, [res.data?.total]);

  const { nextKeys } = useQueryPrefetch({
    keys,
    enabled: options?.enablePrefetch,
    total,
    args: [query.iterator, query.sortBy, query.filterBy],
    serviceMethod: services2.webEventsService.queryWebEvents.bind(services2.webEventsService),
  });

  const mutateDeletedWebEvent = useMutateQueryDataAfterEntityDeleting({
    queryClient,
    nextPageQueryKey: nextKeys,
    total,
    iterator,
    mutate,
    refetch,
    getID: (wE?: WebEvent) => wE?.id,
    getEntities: (r: QueryWebEventsResponse) => r.entities,
    setEntities: (r: QueryWebEventsResponse, newEntities: WebEvent[]) => {
      r.entities = newEntities;
    },
    createNewResponse: (prevResponse: QueryWebEventsResponse) => new QueryWebEventsResponse(prevResponse.serialize()),
  });

  const removeQueries = useCallback(() => {
    queryClient.removeQueries({ queryKey: webEventsQueryKeys });
  }, [queryClient]);

  return {
    ...res,
    webEvents: res.data?.entities || emptyArray,
    total: res.data?.total ?? 0,
    tags: keys,
    mutate,
    mutateDeletedWebEvent,
    removeQueries,
    refetch,
  };
};
